import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Modal from "react-modal";
import {
  FaArrowRight,
  FaUsers,
  FaShieldHalved,
  FaXmark,
  FaPlay,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa6";
import { getEmbedUrl, getYouTubeThumbnail } from "../../utils/helper";
import "./GalleryPage.css";

Modal.setAppElement("#root");

// GALLERY MAIN DATA
const GALLERY_CARDS = [
  {
    id: "national",
    path: "/nationalpromotionsmedia",
    eyebrow: "Across India",
    title: "National Promotions",
    description:
      "Campaigns, trade activations, events and on-ground media coverage across key Indian states.",
    cta: "Explore National Events",
    theme: "national",
    stat: "20+ States Covered",
    image:
      "https://images.unsplash.com/photo-1514222134-b57cbb8ce073?auto=format&fit=crop&w=1200&q=80",
  },
  {
    id: "international",
    path: "/internationalpromotionsmedia",
    eyebrow: "Global Presence",
    title: "International Promotions",
    description:
      "Exhibitions, outreach and brand visibility initiatives that carried Bharat Buildcon to global markets.",
    cta: "Explore International Events",
    theme: "international",
    stat: "15+ Countries Covered",
    image:
      "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=1200&q=80",
  },
];

// VOICE OF BB DATA
const VOICE_ROUTES = {
  stakeholders: "/voiceofstakeholder",
  leaders: "/voiceofindustryleaders",
};

const voices = [
  {
    id: "stakeholders",
    label: "Voice of Stakeholders",
    desc: "Exhibitors, association heads and delegation partners on why the expo floor matters to their business.",
    colorTheme: "saffron",
    image:
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1000&auto=format&fit=crop",
    icon: <FaUsers size={20} />,
  },
  {
    id: "leaders",
    label: "Voice of Industry Leaders",
    desc: "CXOs and manufacturers on where Bharat’s building materials sector is headed next.",
    colorTheme: "blue",
    image:
      "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=1000&auto=format&fit=crop",
    icon: <FaShieldHalved size={20} />,
  },
];

// BEHIND THE MAKING DATA
const videos = [
  {
    id: "tiles",
    title: "How Tiles Are Made",
    subtitle:
      "Inside India’s premium tile lines, from raw clay to a polished finish.",
    gradient: "from-sand",
    videoId: "uWDEJMUoKao",
  },
  {
    id: "faucets",
    title: "How Faucets Are Made",
    subtitle:
      "Molten brass poured, machined and chrome-finished into everyday fixtures.",
    gradient: "from-metal",
    videoId: "FlWrj5Tzwwo",
  },
  {
    id: "sinks",
    title: "How Kitchen Sinks Are Made",
    subtitle:
      "A factory floor tour following stainless steel coil to a finished basin.",
    gradient: "from-steel",
    videoId: "K5NeXhp1Enk",
  },
  {
    id: "sanitaryware",
    title: "How Sanitaryware Is Made",
    subtitle:
      "The full manufacturing process behind everyday ceramic sanitaryware.",
    gradient: "from-ceramic",
    videoId: "bXcWXGjHvvc",
  },
  {
    id: "hardware",
    title: "How Door Hardware Is Made",
    subtitle:
      "Locks, handles and hinges through casting, machining and plating.",
    gradient: "from-metal",
    videoId: "uWDEJMUoKao",
  },
];

function CloseIcon() {
  return <FaXmark size={18} aria-hidden="true" />;
}

function PlayIcon() {
  return <FaPlay size={16} aria-hidden="true" />;
}

function ChevronIcon({ direction = "left" }) {
  return direction === "left" ? (
    <FaChevronLeft size={18} />
  ) : (
    <FaChevronRight size={18} />
  );
}

function VideoModal({ video, onClose }) {
  if (!video) return null;

  return (
    <Modal
      isOpen={!!video}
      onRequestClose={onClose}
      className="gbm-modal__panel"
      overlayClassName="gbm-modal"
      contentLabel={video.title}
    >
      <button
        className="gbm-modal__close"
        onClick={onClose}
        aria-label="Close video"
      >
        <CloseIcon />
      </button>
      <div className="gbm-modal__frame">
        {video.videoId ? (
          <iframe
            src={getEmbedUrl(video.videoId)}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="gbm-modal__placeholder">
            <PlayIcon />
            <span>Video coming soon</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

const GalleryPage = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();

  // Voice of BB Section ref & state
  const gvRef = useRef(null);
  const gvIsVisible = useInView(gvRef, { once: true, amount: 0.15 });

  // Behind the Making Section ref & state
  const gbmRef = useRef(null);
  const gbmIsVisible = useInView(gbmRef, { once: true, amount: 0.15 });
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Hash scrolling support
  useEffect(() => {
    if (hash) {
      const targetId = hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleVoiceNavigate = (id) => {
    navigate(VOICE_ROUTES[id] || `/voices/${id}`);
  };

  const handleVoiceKeyDown = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleVoiceNavigate(id);
    }
  };

  const goPrev = () =>
    setActiveIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));

  const goNext = () =>
    setActiveIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));

  return (
    <div className="gm-page">
      <Helmet>
        <title>Gallery | Bharat Buildcon</title>
      </Helmet>
      {/* ============================================================ */}
      {/* 1. GALLERY HERO & SHOWCASE SECTION                           */}
      {/* ============================================================ */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="gm-hero is-visible"
      >
        <span className="gm-blob gm-blob-saffron" aria-hidden="true" />
        <span className="gm-blob gm-blob-blue" aria-hidden="true" />
        <span className="gm-dot-field" aria-hidden="true" />

        <div className="clean-breadcrumb-wrapper">
          <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="bc-home-link">
              HOME
            </Link>
            <span className="bc-slash">/</span>
            <span className="bc-active-page">MEDIA &amp; GALLERY</span>
          </nav>
        </div>

        <p className="gm-eyebrow">
          <span className="gm-eyebrow-dot" />
          One Nation • One Vision • Global Footprint
        </p>

        <h1 className="gm-hero-title">
          Media <span className="gm-title-highlight">&amp; Gallery</span>
        </h1>

        <p className="gm-hero-copy">
          From local campaigns to international visibility, this space captures
          the journeys, promotions and moments that shaped Bharat Buildcon’s
          story across India and beyond.
        </p>
      </motion.header>

      <section className="gm-gallery-shell">
        <div className="gm-section-head">
          <p className="gm-section-kicker">Promotional Showcase</p>
          <h2 className="gm-section-title">
            Select Gallery <span className="gm-title-highlight">Showcase</span>
          </h2>
        </div>

        <div className="gm-card-grid">
          {/* NATIONAL PROMOTIONS CARD */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="gm-card is-visible"
            onClick={() => handleNavigate(GALLERY_CARDS[0].path)}
          >
            <div className="gm-card-visual national">
              <img
                src={GALLERY_CARDS[0].image}
                alt={GALLERY_CARDS[0].title}
                className="gm-card-bg-img"
              />
              <span className="gm-grid-overlay" aria-hidden="true" />
              <span className="gm-orb gm-orb-a" aria-hidden="true" />
            </div>

            <div className="gm-card-content">
              <p className="gm-card-eyebrow">{GALLERY_CARDS[0].eyebrow}</p>
              <h3 className="gm-card-title">{GALLERY_CARDS[0].title}</h3>
              <p className="gm-card-copy">{GALLERY_CARDS[0].description}</p>

              <button
                className="gm-card-btn"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigate(GALLERY_CARDS[0].path);
                }}
              >
                <span>{GALLERY_CARDS[0].cta}</span>
                <span className="gm-btn-arrow">
                  <FaArrowRight />
                </span>
              </button>
            </div>
          </motion.article>

          {/* INTERNATIONAL PROMOTIONS CARD */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.14 }}
            className="gm-card is-visible"
            onClick={() => handleNavigate(GALLERY_CARDS[1].path)}
          >
            <div className="gm-card-visual international">
              <img
                src={GALLERY_CARDS[1].image}
                alt={GALLERY_CARDS[1].title}
                className="gm-card-bg-img"
              />
              <span className="gm-grid-overlay" aria-hidden="true" />
              <span className="gm-orb gm-orb-b" aria-hidden="true" />
            </div>

            <div className="gm-card-content">
              <p className="gm-card-eyebrow">{GALLERY_CARDS[1].eyebrow}</p>
              <h3 className="gm-card-title">{GALLERY_CARDS[1].title}</h3>
              <p className="gm-card-copy">{GALLERY_CARDS[1].description}</p>

              <button
                className="gm-card-btn"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigate(GALLERY_CARDS[1].path);
                }}
              >
                <span>{GALLERY_CARDS[1].cta}</span>
                <span className="gm-btn-arrow">
                  <FaArrowRight />
                </span>
              </button>
            </div>
          </motion.article>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 2. VOICE OF BHARAT BUILDCON SECTION                          */}
      {/* ============================================================ */}
      <section
        className={`gv-section ${gvIsVisible ? "is-visible" : ""}`}
        ref={gvRef}
        id="voice-of-bharat-buildcon"
      >
        <div className="gv-bg-grid" aria-hidden="true" />
        <div className="gv-blob gv-blob-saffron" aria-hidden="true" />
        <div className="gv-blob gv-blob-blue" aria-hidden="true" />

        <header className="gv-head">
          <p className="gv-eyebrow">
            <span className="gv-eyebrow-dot" />
            AUDIO TESTIMONIALS
          </p>
          <h2 className="gv-title">
            Voice of <span className="gv-title-gradient">Bharat Buildcon</span>
          </h2>
          <p className="gv-slogan">
            <span>One Nation,</span>
            <mark>One Expo</mark>
          </p>
        </header>

        <div className="gv-cards-grid">
          {voices.map((v, i) => (
            <div
              className={`gv-card gv-card--${v.colorTheme}`}
              key={v.id}
              style={{ transitionDelay: `${i * 140}ms` }}
              role="button"
              tabIndex={0}
              aria-label={`${v.label} — listen`}
              onClick={() => handleVoiceNavigate(v.id)}
              onKeyDown={(e) => handleVoiceKeyDown(e, v.id)}
            >
              <img
                src={v.image}
                alt={v.label}
                className="gv-card__bg-img"
                loading="lazy"
              />
              <div className="gv-card__overlay" />

              <div className="gv-card__content">
                <h3 className="gv-card__title">{v.label}</h3>
                <p className="gv-card__desc">{v.desc}</p>

                <div className="gv-card__footer">
                  <div className="gv-card__cta">
                    <span>Listen Voices</span>
                    <FaArrowRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* 3. BEHIND THE MAKING SECTION                                 */}
      {/* ============================================================ */}
      <section
        className={`gbm-section ${gbmIsVisible ? "is-visible" : ""}`}
        ref={gbmRef}
        id="behind-the-making"
      >
        <span className="gbm-blob gbm-blob-saffron" aria-hidden="true" />
        <span className="gbm-blob gbm-blob-blue" aria-hidden="true" />
        <span className="gbm-blob gbm-blob-green" aria-hidden="true" />
        <span className="gbm-blueprint" aria-hidden="true" />

        <header className="gbm-head">
          <p className="gbm-eyebrow">Factory Files</p>
          <h2 className="gbm-title">
            Behind <span className="gbm-title-highlight">The Making</span>
          </h2>
          <p className="gbm-sub">
            Raw material to finished product — short factory tours across every
            product line.
          </p>
        </header>

        <div className="gbm-stage-wrapper">
          <div className="gbm-cards-track">
            <button
              type="button"
              className="gbm-arrow gbm-arrow--prev"
              onClick={goPrev}
              aria-label="Previous video"
            >
              <ChevronIcon direction="left" />
            </button>

            {videos.map((v, i) => {
              const isCenter = i === activeIndex;
              const isLeft =
                i === (activeIndex - 1 + videos.length) % videos.length;
              const isRight = i === (activeIndex + 1) % videos.length;

              let posClass = "hidden";
              if (isCenter) posClass = "active-center";
              else if (isLeft) posClass = "pos-left";
              else if (isRight) posClass = "pos-right";

              return (
                <div
                  className={`gbm-card gbm-card--${v.gradient} ${posClass}`}
                  key={v.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Play video: ${v.title}`}
                  onClick={() =>
                    isCenter ? setActiveVideo(v) : setActiveIndex(i)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      isCenter ? setActiveVideo(v) : setActiveIndex(i);
                    }
                  }}
                >
                  <div className="gbm-card__art">
                    {v.videoId ? (
                      <img
                        src={getYouTubeThumbnail(v.videoId)}
                        alt={v.title}
                        className="gbm-card__thumbnail"
                      />
                    ) : null}
                    <div className="gbm-card__scrim" aria-hidden="true" />
                    <span className="gbm-play">
                      <PlayIcon />
                    </span>
                  </div>
                  <div className="gbm-card__body">
                    <h3>{v.title}</h3>
                    <p>{v.subtitle}</p>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className="gbm-arrow gbm-arrow--next"
              onClick={goNext}
              aria-label="Next video"
            >
              <ChevronIcon direction="right" />
            </button>
          </div>
        </div>

        {activeVideo && (
          <VideoModal
            video={activeVideo}
            onClose={() => setActiveVideo(null)}
          />
        )}
      </section>
    </div>
  );
};

export default GalleryPage;
